import { Bike, Bus, Car, Footprints, CarFront, MapPin, ExternalLink, AlertCircle } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const modeIcons = [Bike, Car, Bus, Footprints];

/** Only allow safe absolute http(s) links coming from CMS content. */
function safeUrl(url: string | undefined, fallback: string) {
  if (!url) return fallback;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:" ? url : fallback;
  } catch {
    return fallback;
  }
}

export function Travel() {
  const { t } = useI18n();
  const modes = t.travel.modes.map((m, i) => ({ ...m, icon: modeIcons[i] ?? Bike }));
  const fp = t.travel.freeParking;
  const mapsUrl = safeUrl(t.travel.mapsUrl, "https://maps.app.goo.gl/TM6ntXiRm8DtR1RP7");
  const parkingUrl = safeUrl(
    fp.mapsUrl,
    "https://www.google.com/maps/search/?api=1&query=52.206375,5.919837",
  );
  return (
    <section id="bereikbaarheid" className="relative py-24 md:py-32">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="font-mono-cinema text-[10px] text-gold">{t.travel.eyebrow}</p>
            <h2 className="mt-5 font-display text-4xl font-medium leading-[1.05] md:text-5xl">
              {t.travel.title1}
              <span className="block italic text-gold">{t.travel.title2}</span>
            </h2>
            <p className="mt-5 text-foreground/70">
              {t.travel.body}
            </p>
            <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-card p-1">
              <div className="flex h-48 items-center justify-center rounded-xl bg-[radial-gradient(circle_at_30%_20%,oklch(0.22_0.05_175/0.5),transparent_60%),radial-gradient(circle_at_70%_70%,oklch(0.86_0.16_72/0.15),transparent_60%)]">
                <div className="text-center">
                  <div className="font-mono-cinema text-[10px] text-foreground/75">{t.travel.mapLabel}</div>
                  <div className="mt-2 font-display text-foreground/70">{t.travel.address}</div>
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-block font-mono-cinema text-[10px] text-gold hover:text-amber-glow"
                  >
                    {t.common.openInMaps}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {/* Free parking callout — first parking option */}
            <div
              id="parkeren"
              className="relative mb-5 overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-card to-card p-6 md:p-7"
            >
              <div className="absolute right-0 top-0 h-28 w-28 bg-[radial-gradient(circle_at_top_right,oklch(0.72_0.17_155/0.18),transparent_70%)]" />
              <div className="relative flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                  <CarFront className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-950 shadow-[0_0_14px_rgba(16,185,129,0.45)]">
                      {fp.badge}
                    </span>
                  </div>
                  <h3 className="mt-3 font-display text-xl leading-snug md:text-2xl">{fp.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/75">{fp.body}</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground/60">{fp.walk}</p>
                  <p className="mt-2.5 flex items-start gap-2 text-xs leading-relaxed text-foreground/50">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-glow/70" />
                    {fp.note}
                  </p>
                  <a
                    href={parkingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-emerald-950 transition-all hover:bg-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.5)]"
                  >
                    <MapPin className="h-4 w-4" />
                    {fp.cta}
                    <ExternalLink className="h-3.5 w-3.5 opacity-70" />
                  </a>
                  <p className="mt-3 text-[11px] leading-relaxed text-foreground/40">{fp.disclaimer}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {modes.map((m) => (
                <div
                  key={m.title}
                  className="rounded-2xl border border-border bg-card p-7 transition-colors hover:border-gold/40"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-gold/10 text-gold">
                      <m.icon className="h-5 w-5" />
                    </div>
                    <span className="font-mono-cinema text-[10px] text-gold">{m.tag}</span>
                  </div>
                  <h3 className="mt-5 font-display text-xl">{m.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/65">{m.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
